import React, { useEffect, useRef, useState } from 'react';

// https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/readyState
enum HTMLMediaElementReadyState {
	HAVE_NOTHING = 0,
	HAVE_METADATA,
	HAVE_CURRENT_DATA,
	HAVE_FUTURE_DATA,
	HAVE_ENOUGH_DATA,
}

export enum PlaybackState {
	Loading,
	Stopped,
	Paused,
	Playing,
}

export const useVideoPlayer = () => {
	const [state, setState] = useState<PlaybackState>(PlaybackState.Loading);
	const videoRef = useRef<HTMLVideoElement>(null);

	useEffect(() => {
		if (!videoRef.current) return;
		videoRef.current.addEventListener('ended', handleOnVideoEnded);
		videoRef.current.addEventListener('canplaythrough', handleOnVideoCanPlay);

		// on iOS, the video won't load unless explicitly requested here
		videoRef.current.load();

		// If already loaded before we set this event listener, set ready for playback
		if (videoRef.current.readyState === HTMLMediaElementReadyState.HAVE_ENOUGH_DATA)
			setState(PlaybackState.Stopped);
	}, [videoRef]);

	const toggleVideoPlayback = () => {
		switch (state) {
			case PlaybackState.Stopped:
			case PlaybackState.Paused:
				(async () => {
					try {
						if (!videoRef.current) return;
						await videoRef.current.play();
						setState(PlaybackState.Playing);
					} catch (error) {
						console.error("Couldn't play video", error);
					}
				})();
				return;
			case PlaybackState.Playing:
				(async () => {
					videoRef.current?.pause();
					setState(PlaybackState.Paused);
				})();
				return;
		}
	};

	const handleOnVideoEnded = () => {
		if (videoRef.current) videoRef.current.currentTime = 0;
		setState(PlaybackState.Stopped);
	};

	const handleOnVideoCanPlay = () => {
		setState(PlaybackState.Stopped);
	};

	return {
		toggleVideoPlayback,
		videoRef,
		state,
	};
};
